import { useState, useEffect } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [subscriptionData, setSubscriptionData] = useState(null);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      verifySubscription();
    } else {
      setStatus("error");
    }
  }, [sessionId]);

  const verifySubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscription/verify/${sessionId}`);
      setSubscriptionData(response.data);
      
      if (response.data.status === "active") {
        setStatus("success");
      } else if (response.data.status === "pending") {
        // Poll again
        setTimeout(verifySubscription, 2000);
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("Failed to verify subscription:", err);
      // If unauthorized, redirect to login
      if (err.response?.status === 401) {
        navigate("/login?redirect=/subscription-success?session_id=" + sessionId);
        return;
      }
      setStatus("error");
    }
  };

  const formatPlan = (plan) => {
    if (plan === "yearly") return "Yearly ($55/year)";
    return "Monthly ($10/month)";
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 text-center" data-testid="subscription-success-page">
        {status === "loading" && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Activating Your Subscription</h1>
            <p className="text-white/60">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-4">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">Welcome to LedgerAMT Pro!</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Subscription Activated!</h1>
            <p className="text-white/60 mb-6">
              Thank you for subscribing. You now have access to all features.
            </p>
            
            {subscriptionData && (
              <div className="bg-white/5 rounded-lg p-4 mb-6 text-left border border-white/10">
                <div className="flex justify-between items-center py-2 border-b border-white/10">
                  <span className="text-white/60">Plan</span>
                  <span className="font-semibold text-white">
                    {formatPlan(subscriptionData.plan)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-white/60">Status</span>
                  <span className="inline-flex items-center gap-1 text-emerald-400 font-medium">
                    <CheckCircle2 className="w-4 h-4" />
                    Active
                  </span>
                </div>
                {subscriptionData.current_period_end && (
                  <div className="flex justify-between items-center py-2 border-t border-white/10">
                    <span className="text-white/60">Renews</span>
                    <span className="text-white">
                      {new Date(subscriptionData.current_period_end).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            <Link to="/dashboard">
              <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-lg group">
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Something Went Wrong</h1>
            <p className="text-white/60 mb-6">
              We couldn't activate your subscription. Please try again or contact support.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/pricing">
                <Button variant="outline" className="w-full sm:w-auto border-white/20 text-white hover:bg-white/10">
                  Back to Pricing
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white">
                  Contact Support
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
